import { useEffectOnValueChange, useStateWithLocalStorage } from '@timelyapp/react';
import { Container } from 'components/ui-blocks/container';
import type { QueryStateFromSchema } from 'hooks/useQueryParameterStateSync';
import { useQueryParameterStateSync } from 'hooks/useQueryParameterStateSync';
import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getAccountFeatures } from 'redux/selectors/accounts';
import { getCanSeeTasks } from 'redux/selectors/subscriptions';
import type { ThunkDispatch } from 'types/redux';
import * as z from 'zod';
import ForecastGanttChart from './components/ForecastGanttChart';
import ForecastListHeader from './components/ForecastListHeader';
import type { ForecastQueryFiltersContext } from './components/ForecastQueryFilters';
import ForecastSplashMessage from './components/ForecastSplashMessage';
import ForecastsByAssigneeTable from './components/ForecastsByAssigneeTable';
import ForecastsByProjectTable from './components/ForecastsByProjectTable';
import { useActiveForecastViewType } from './hooks/useActiveForecastViewType';
import { useForecastQueryStorage } from './hooks/useForecastQueryStorage';
import {
  fetchPaginatedForecastsSmartly,
  resetForecastPaginationState,
  setQuery,
} from './redux/modules/forecast.actions';
import { getForecastQuery } from './redux/modules/forecast.selectors';
import type { ForecastQuery } from './redux/modules/forecast.types';

interface Props {
  activeView: 'assignee' | 'project';
  context?: ForecastQueryFiltersContext;
}

export const ForecastPage: React.FC<Props> = (props: Props) => {
  const { activeView, context } = props;
  const dispatch = useDispatch<ThunkDispatch>();
  const query = useSelector(getForecastQuery);
  const navigate = useNavigate();
  const { activeViewType, setActiveViewType } = useActiveForecastViewType();
  const [showAvailability, setShowAvailability] = useStateWithLocalStorage<boolean>(
    true,
    'timely-tasks_show-capacities'
  );

  const hasAccessToTasks = useSelector(getAccountFeatures).tasks;
  const canSeeTasks = useSelector(getCanSeeTasks);
  let view = <ForecastGanttChart view={activeView} showAvailability={showAvailability} />;
  if (activeViewType === 'list') {
    if (activeView === 'project') {
      view = <ForecastsByProjectTable />;
    } else {
      view = <ForecastsByAssigneeTable />;
    }
  }

  useForecastQueryStorage();

  useEffectOnValueChange({ query, activeViewType }, () => {
    if (activeViewType === 'list') {
      dispatch(resetForecastPaginationState());
      dispatch(fetchPaginatedForecastsSmartly());
    }
  });

  const querySyncSchema = useMemo(
    () => ({
      view: z.union([z.literal('timeline'), z.literal('list')]),
      projectIds:
        context === 'project'
          ? z.never()
          : z.union([
              z.array(z.number()),
              z.literal('all'),
              z.literal('archived'),
              z.literal('active'),
            ]),
      userIds: context === 'assignee' ? z.never() : z.nullable(z.array(z.nullable(z.number()))),
      teamIds: z.nullable(z.array(z.number())),
      showCompleted: z.boolean(),
      showAvailability: z.boolean(),
      onlyShowProjectAssignees: z.boolean(),
      since: z.nullable(z.string()),
      until: z.nullable(z.string()),
      dateSpan: z.union([
        z.literal('day'),
        z.literal('week'),
        z.literal('month'),
        z.literal('custom'),
      ]),
    }),
    [context]
  );
  const handleSetQuery = useCallback(
    ({
      view,
      showAvailability,
      ...query
    }: Partial<QueryStateFromSchema<typeof querySyncSchema>>) => {
      if (Object.keys(query).length > 0) {
        // Convert ReportProjectIds to Array<number> for forecast
        const convertedQuery: Partial<ForecastQuery> = {};
        Object.entries(query).forEach(([key, value]) => {
          if (key === 'projectIds' && value != null && !Array.isArray(value)) {
            // For forecast, we only support specific project IDs, not 'all'/'active'/'archived'
            convertedQuery.projectIds = [];
          } else if (key !== 'projectIds') {
            (convertedQuery as any)[key] = value;
          } else {
            convertedQuery.projectIds = value as Array<number> | undefined;
          }
        });
        dispatch(setQuery(convertedQuery));
      }
      if (view != null) {
        setActiveViewType(view);
      }
      if (showAvailability != null) {
        setShowAvailability(showAvailability);
      }
    },
    [dispatch, setActiveViewType, setShowAvailability]
  );
  const queryState = useMemo(
    () => ({
      ...query,
      view: activeViewType,
      showAvailability,
    }),
    [query, activeViewType, showAvailability]
  );
  useQueryParameterStateSync(querySyncSchema, queryState, handleSetQuery);

  if (!hasAccessToTasks) {
    if (!canSeeTasks) {
      navigate('/calendar');
    }
    return (
      <Container>
        <ForecastSplashMessage />
      </Container>
    );
  }
  return (
    <>
      <Container
        style={{
          padding: '20px 8%',
        }}
      >
        <ForecastListHeader
          activeViewType={activeViewType}
          setActiveViewType={setActiveViewType}
          context={context}
          showAvailability={showAvailability}
          onSetShowAvailability={setShowAvailability}
          showCapacitiesCheckbox={activeViewType === 'timeline' && activeView === 'assignee'}
          activeView={activeView}
        />
      </Container>
      <Container
        flexGrow
        style={{
          padding: activeViewType === 'timeline' ? 0 : '0 8%',
        }}
      >
        {view}
      </Container>
    </>
  );
};
